import { Box, Typography, makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import React from 'react'

const useStyles = makeStyles((theme) => ({
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    marginBottom: theme.spacing(2)
  },
  ok: { backgroundColor: '#e8f5e9' },
  over: { backgroundColor: '#ffebee' },
  none: { backgroundColor: '#fff8e1' },
  metric: { display: 'flex', flexDirection: 'column' },
  value: { fontWeight: 700 },
  capNote: { fontSize: 12, color: theme.palette.text.secondary }
}))

const formatRupee = (n) =>
  n === null || n === undefined || n === ''
    ? '—'
    : `₹${Number(n).toLocaleString('en-IN')}`

// Shows "₹X of ₹Y used, ₹Z left" for a category's monthly budget.
// `inDraft` (optional) = typed-but-unsubmitted rows on the page; when present,
// Remaining is shown net of it so the centre sees what they'd have left if
// they submitted what's on screen.
// `hardCap` (optional) = this category cannot be overspent, so Remaining is a
// wall rather than a guide. Only petty cash passes it. Without the note the
// centre meets the cap for the first time as a failed save, having already
// paid the cash out.
const BudgetBanner = ({ budget, used, remaining, inDraft, label, hardCap }) => {
  const classes = useStyles()

  const noBudget = budget === null || budget === undefined
  const draftAmount = Number(inDraft) > 0 ? Number(inDraft) : 0
  const netRemaining =
    remaining === null || remaining === undefined
      ? remaining
      : Number(remaining) - draftAmount
  const isOver = !noBudget && Number(netRemaining) < 0
  const variant = noBudget ? classes.none : isOver ? classes.over : classes.ok

  return (
    <Box className={`${classes.banner} ${variant}`}>
      {label && <Typography variant="subtitle2">{label}</Typography>}
      {noBudget ? (
        <Typography variant="body2">
          No budget set for this centre / month — ask an admin to set one.
        </Typography>
      ) : (
        <>
          <div className={classes.metric}>
            <Typography variant="caption" color="textSecondary">
              Budget
            </Typography>
            <Typography className={classes.value}>
              {formatRupee(budget)}
            </Typography>
          </div>
          <div className={classes.metric}>
            <Typography variant="caption" color="textSecondary">
              Used
            </Typography>
            <Typography className={classes.value}>
              {formatRupee(used)}
            </Typography>
          </div>
          {draftAmount > 0 && (
            <div className={classes.metric}>
              <Typography variant="caption" color="textSecondary">
                In draft (not submitted)
              </Typography>
              <Typography className={classes.value} style={{ color: '#ef6c00' }}>
                {formatRupee(draftAmount)}
              </Typography>
            </div>
          )}
          <div className={classes.metric}>
            <Typography variant="caption" color="textSecondary">
              {draftAmount > 0 ? 'Remaining after draft' : 'Remaining'}
            </Typography>
            <Typography
              className={classes.value}
              style={{ color: isOver ? '#c62828' : '#2e7d32' }}>
              {formatRupee(netRemaining)}
            </Typography>
          </div>
          {hardCap && (
            <Typography className={classes.capNote}>
              {isOver
                ? 'Over the budget — ask HQ to move budget into petty cash before recording more.'
                : 'You cannot record more than the budget. Ask HQ to move budget in if you need more.'}
            </Typography>
          )}
        </>
      )}
    </Box>
  )
}

BudgetBanner.propTypes = {
  budget: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  used: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  remaining: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hardCap: PropTypes.bool,
  inDraft: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string
}

export default BudgetBanner
